import { formatDayMonth } from '@rental/i18n'
import { useEffect, useState } from 'react'
import { useLocale } from '../i18n'
import { onResume } from '../resume'
import { romeNow, untilNextMinute } from '../time'

/**
 * UI-2a: the time and the day on Ischia, on the lock screen (the owner's
 * mock-up). It turns on the minute and is read again when the app comes back
 * to the front (`resume.ts`: the page's signal or the iPad shell's), so a
 * screen left asleep never shows an old time. Never announced: it is not news.
 */
export function RomeClock({ className }: { className?: string }) {
  const { locale } = useLocale()
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    let timer = 0
    // Safe to call twice for one return, as resume.ts asks: the last timer is replaced.
    const tick = () => {
      window.clearTimeout(timer)
      const at = new Date()
      setNow(at)
      timer = window.setTimeout(tick, untilNextMinute(at.getTime()))
    }
    timer = window.setTimeout(tick, untilNextMinute(Date.now()))
    const stopResume = onResume(tick)
    return () => {
      window.clearTimeout(timer)
      stopResume()
    }
  }, [])
  const { date, time } = romeNow(now)
  // The one thing drawn on the photograph itself, with no pane, as in the owner's
  // mock-up: how it stands out from the pixels behind it is measured instead
  // (`expectReadableOnPhoto`, e2e/support.ts). The day is in the text's own ink, not
  // the muted one: muted, it fell to 3.5:1 over the photograph's darkest corner.
  return (
    <div data-on-photo className={className}>
      <time
        dateTime={`${date}T${time}`}
        className="figures block text-4xl leading-display font-bold text-brand-strong"
      >
        {time}
      </time>
      <p className="mt-1 text-text">{formatDayMonth(locale, date)}</p>
    </div>
  )
}
